import { getOrders } from './ApiService'

/**
 * Order statuses of a draft (not yet booked) MA/PTA order in the orders search.
 * Oracle leaves the draft unbooked until checkout's Place order; GOS reports it
 * as Submitted, the Data Platform may report Draft or Entered.
 */
const DRAFT_ORDER_STATUSES = ['submitted', 'draft', 'entered']

/**
 * CLOS-14565: Oracle's patient summary can show One more step to go without an
 * order number while the draft order exists in EBS. Finds the patient's most
 * recent draft order (for the programme, when known) in the institute's
 * orders search. Returns undefined when there is none.
 */
export const findDraftOrderNumber = async (
  patientNumber: string | undefined,
  programId?: number | string | null
): Promise<string | undefined> => {
  const wanted = patientNumber?.trim()
  if (!wanted) {
    return undefined
  }

  const response = await getOrders({
    query: '',
    filter: {
      start: null,
      end: null,
      orderStatuses: [],
      orderNumberStart: 0,
      orderNumberEnd: 2147483647, // max value of a 32 bit integer
      purchaseNumberStart: null,
      purchaseNumberEnd: null,
      itemGenerics: [],
      patients: [{ patientNumber: wanted }],
      showMyOrders: false
    },
    pagination: { skip: 0, take: 10 },
    sorting: { sortBy: 'orderedDate', order: 'desc' }
  })

  // Sorted newest first, so the first match is the most recent draft.
  const draft = response?.data?.result?.find(
    (order) =>
      order.patientNumber?.trim().toUpperCase() === wanted.toUpperCase() &&
      (!programId ||
        !order.programId ||
        Number(order.programId) === Number(programId)) &&
      DRAFT_ORDER_STATUSES.includes(
        order.orderStatus?.trim().toLowerCase() ?? ''
      )
  )

  return draft?.orderNumber ? String(draft.orderNumber) : undefined
}

/** The known order number, otherwise the patient's draft order, if any. */
export const resolveCheckoutOrderNumber = async (
  knownOrderNumber: string | undefined,
  patientNumber: string | undefined,
  programId?: number | string | null
): Promise<string | undefined> =>
  knownOrderNumber ||
  findDraftOrderNumber(patientNumber, programId).catch(() => undefined)
